/**
 * File: useRoleQueries.ts
 * Path: apps/admin/src/modules/auth/hooks/useRoleQueries.ts
 * Description: The signed-in role and what it may do, from the custom claims.
 */

import { useCallback } from "react";
import { useAuth } from "@/modules/auth/components/AuthProvider";
import {
  isAdminRole,
  isStaffRole,
  roleCan,
  roleFromClaims,
  type Permission,
  type UserRole,
} from "@idk/types";

export interface UseRoleReturn {
  role: UserRole | null;
  /** Whether the role holds `permission` (packages/types ROLE_PERMISSIONS). */
  can: (permission: Permission) => boolean;
  /** Super admin or admin. */
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** May edit website content. */
  isEditor: boolean;
  /** No staff role: refused by the admin app. */
  isViewer: boolean;
}

export const useRole = (): UseRoleReturn => {
  const { claims } = useAuth();
  const role = claims ? roleFromClaims(claims) : null;
  const effective: UserRole = role ?? "viewer";

  const can = useCallback(
    (permission: Permission) => roleCan(effective, permission),
    [effective],
  );

  return {
    role,
    can,
    isAdmin: isAdminRole(effective),
    isSuperAdmin: effective === "superAdmin",
    isEditor: roleCan(effective, "content"),
    isViewer: !isStaffRole(effective),
  };
};
